// src/Pages/Split/SplitMembers.jsx
// ─────────────────────────────────────────────────────────────────────────
// Blitz Split — the people side of the card (group-chat pattern):
//   MemberBubble / AddBubble / GhostMeBubble   the strip
//   CreatorResultRow + UnifiedSearch           one search for everyone
//   AmountCard                                 shares, ⚡ pre-accept, presets
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Paper, Avatar, IconButton,
  InputAdornment, Chip, Alert, Stack, Rating, Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add, Close as CloseIcon, Search as SearchIcon, Bolt,
  Email as EmailIcon, Person as PersonIcon, People as FollowersIcon,
  Schedule as LaterIcon,
  BookmarkBorder as PresetOffIcon, Bookmark as PresetOnIcon,
  Instagram as InstagramIcon, YouTube as YouTubeIcon,
  Twitter as TwitterIcon, Podcasts as PodcastsIcon, Cast as CastIcon,
  Link as LinkIcon,
} from "@mui/icons-material";

import {
  parseLocalFloat, calculateTotalFollowing, getAllPlatformLinks,
} from "../../../Utils/constants";
import { formatCompactNumber } from "../../../Utils/formatNumbers";
import profilePhoto from "../../../Components/globalAssets/ppfLogo.png";
import TikTokIcon from "../../../Components/Icons/TikTok";
import TwitchIcon from "../../../Components/Icons/Twitch";

import {
  SPLIT_YELLOW, SPLIT_YELLOW_DARK, SPLIT_YELLOW_DEEP, SPLIT_INK,
  SPLIT_TINT, SPLIT_BORDER, fadeUp, popIn, fmt, isEmailish,
} from "./SplitTokens";
import { presetKeyFor, presetKeyForCreator, presetKeyForEmail } from "./SplitData";

const socialIcons = {
  TikTok: <TikTokIcon />,
  Instagram: <InstagramIcon fontSize="inherit" />,
  Youtube: <YouTubeIcon fontSize="inherit" />,
  X: <TwitterIcon fontSize="inherit" />,
  Twitch: <TwitchIcon />,
  Podcasts: <PodcastsIcon fontSize="inherit" />,
  Kick: <CastIcon fontSize="inherit" />,
};
const platformColors = {
  Youtube: "#FF0000", Instagram: "#E4405F", TikTok: "#FE2C55",
  Podcasts: "#9C27B0", Twitch: "#9146FF", Kick: "#53FC18", X: "#1DA1F2",
};

// ═══════════════════════════════════════════════════════════════════════════
// Strip bubbles
// ═══════════════════════════════════════════════════════════════════════════
export function MemberBubble({ row, onRemove }) {
  const isMe = row.kind === "me";
  const isCeleb = row.creator?.tier_level === "celebrity";
  const photo = isMe ? null : (row.creator?.pfphref || null);
  const ring = isCeleb
    ? "linear-gradient(45deg, #FFD700, #FFA500, #FF6347, #FFD700)"
    : `linear-gradient(135deg, ${isMe ? alpha(SPLIT_INK, 0.7) : SPLIT_YELLOW}, ${alpha(isMe ? SPLIT_INK : SPLIT_YELLOW, 0.35)})`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
      width: 64, flexShrink: 0, position: "relative",
      animation: `${popIn} 0.22s cubic-bezier(0.34,1.4,0.64,1)` }}>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", inset: -3, borderRadius: "50%", background: ring }} />
        <Avatar src={photo || undefined}
          sx={{ width: 48, height: 48, bgcolor: isMe ? "grey.800" : alpha(SPLIT_YELLOW, 0.15),
            color: isMe ? "common.white" : SPLIT_YELLOW_DEEP, fontWeight: 800, fontSize: "1rem",
            position: "relative", border: "3px solid white" }}>
          {!photo && (isMe ? "Y" : (row.name?.[0] || "?").toUpperCase())}
        </Avatar>
        {row.blitzpayPre && (
          <Tooltip title="BlitzPay pre-accepted — early payout elected on their slice" arrow>
            <Box sx={{ position: "absolute", bottom: -3, right: -3, width: 18, height: 18,
              borderRadius: "50%", bgcolor: SPLIT_YELLOW, border: "2px solid white",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2,
              animation: `${popIn} 0.2s ease` }}>
              <Bolt sx={{ fontSize: 11, color: SPLIT_INK }} />
            </Box>
          </Tooltip>
        )}
        <IconButton size="small" onClick={() => onRemove(row.rowId)} aria-label="Remove" sx={{
          position: "absolute", top: -5, right: -5, zIndex: 3, width: 18, height: 18,
          bgcolor: "white", border: "1.5px solid", borderColor: "grey.200",
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)", p: 0,
          "&:hover": { bgcolor: "error.main", borderColor: "error.main", "& svg": { color: "white" } },
          transition: "all 0.15s ease",
        }}>
          <CloseIcon sx={{ fontSize: 10, color: "text.disabled" }} />
        </IconButton>
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.64rem", lineHeight: 1.1,
        textAlign: "center", maxWidth: 64 }} noWrap>
        {isMe ? "You" : row.name}
      </Typography>
    </Box>
  );
}

export function AddBubble({ active, onClick }) {
  return (
    <Box onClick={onClick} sx={{ display: "flex", flexDirection: "column", alignItems: "center",
      gap: 0.5, width: 64, flexShrink: 0, cursor: "pointer" }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: "50%",
        border: "2px dashed", borderColor: active ? SPLIT_YELLOW_DARK : alpha(SPLIT_YELLOW, 0.5),
        bgcolor: active ? SPLIT_TINT : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.18s ease",
        "&:hover": { borderColor: SPLIT_YELLOW_DARK, bgcolor: SPLIT_TINT, transform: "scale(1.06)" },
      }}>
        <Add sx={{ fontSize: 22, color: SPLIT_YELLOW_DARK,
          transform: active ? "rotate(45deg)" : "none", transition: "transform 0.18s ease" }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.64rem",
        color: SPLIT_YELLOW_DEEP }}>
        {active ? "Close" : "Add"}
      </Typography>
    </Box>
  );
}

export function GhostMeBubble({ onClick }) {
  return (
    <Box onClick={onClick} sx={{ display: "flex", flexDirection: "column", alignItems: "center",
      gap: 0.5, width: 64, flexShrink: 0, cursor: "pointer", opacity: 0.55,
      transition: "opacity 0.15s", "&:hover": { opacity: 1 } }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: "50%", border: "2px dashed", borderColor: "grey.300",
        display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
        "&:hover": { borderColor: "grey.500" },
      }}>
        <PersonIcon sx={{ fontSize: 20, color: "text.disabled" }} />
        <Box sx={{ position: "absolute", bottom: -2, right: -2, width: 16, height: 16,
          borderRadius: "50%", bgcolor: "grey.700", display: "flex", alignItems: "center",
          justifyContent: "center", border: "2px solid white" }}>
          <Add sx={{ fontSize: 10, color: "white" }} />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.64rem",
        color: "text.disabled" }}>
        My share
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CreatorResultRow — rich search result, advertises saved presets
// ═══════════════════════════════════════════════════════════════════════════
export function CreatorResultRow({ creator, onPick, delay = 0, matchedEmail = false, preset = null }) {
  const followers = calculateTotalFollowing(creator);
  const avgReview = creator.average_rating || 0;
  const reviewCount = creator.review_count || 0;
  const platforms = getAllPlatformLinks(creator);
  const isCeleb = creator.tier_level === "celebrity";
  const ring = isCeleb
    ? "linear-gradient(45deg, #FFD700, #FFA500, #FF6347, #FFD700)"
    : `linear-gradient(135deg, ${SPLIT_YELLOW}, ${alpha(SPLIT_YELLOW, 0.4)})`;

  return (
    <Paper variant="outlined" onClick={() => onPick(creator)} sx={{
      p: 1.5, borderRadius: 2.5, cursor: "pointer", display: "flex", alignItems: "center",
      gap: 1.5, borderColor: "grey.200", transition: "all 0.18s ease",
      animation: `${fadeUp} 0.2s ease both`, animationDelay: `${delay}s`,
      "&:hover": { borderColor: SPLIT_YELLOW_DARK, bgcolor: alpha(SPLIT_YELLOW, 0.025),
        transform: "translateX(3px)", boxShadow: `0 2px 12px ${alpha(SPLIT_YELLOW, 0.18)}` },
    }}>
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%", background: ring }} />
        <Avatar src={creator.pfphref || profilePhoto} alt={creator.creator}
          sx={{ width: 42, height: 42, position: "relative", border: "2.5px solid white" }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.9rem" }} noWrap>
            @{creator.display_name || creator.creator}
          </Typography>
          {isCeleb && (
            <Chip label="CELEBRITY" size="small" sx={{ height: 16, fontSize: "0.55rem",
              fontWeight: 800, background: "linear-gradient(45deg, #FFD700, #FFA500)",
              color: "#000", "& .MuiChip-label": { px: 0.6 } }} />
          )}
          {preset && (
            <Tooltip arrow title="Saved default — applies when you add them">
              <Chip
                icon={<PresetOnIcon sx={{ fontSize: "11px !important", color: `${SPLIT_INK} !important` }} />}
                label={`${preset.amount ? fmt(parseLocalFloat(preset.amount)) : "Preset"}${preset.blitzpayPre ? " ⚡" : ""}`}
                size="small"
                sx={{ height: 17, fontSize: "0.58rem", fontWeight: 800,
                  bgcolor: SPLIT_TINT, color: SPLIT_INK, border: "1px solid",
                  borderColor: SPLIT_BORDER, "& .MuiChip-label": { px: 0.6 } }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <FollowersIcon sx={{ fontSize: 12, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary"
              sx={{ fontSize: "0.68rem", fontWeight: 600 }}>
              {formatCompactNumber(followers)}
            </Typography>
          </Box>
          {avgReview > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Rating value={Math.round(avgReview)} readOnly size="small"
                sx={{ fontSize: 12, "& .MuiRating-iconFilled": { color: SPLIT_YELLOW_DARK } }} />
              <Typography variant="caption" color="text.disabled"
                sx={{ fontSize: "0.65rem" }}>({reviewCount})</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 0.4 }}>
            {platforms.slice(0, 4).map((p) => (
              <Box key={p.platform} sx={{ fontSize: 12,
                color: platformColors[p.platform] || "text.disabled",
                display: "flex", alignItems: "center" }}>
                {socialIcons[p.platform] || <LinkIcon fontSize="inherit" />}
              </Box>
            ))}
          </Box>
        </Box>
        {matchedEmail && creator.email && (
          <Typography variant="caption" sx={{ display: "block", fontSize: "0.66rem",
            color: SPLIT_YELLOW_DEEP, fontWeight: 600, mt: 0.25 }} noWrap>
            <EmailIcon sx={{ fontSize: 10, mr: 0.4, verticalAlign: "middle" }} />{creator.email}
          </Typography>
        )}
      </Box>
      <Chip label="Add" size="small" sx={{ fontWeight: 700, fontSize: "0.72rem", height: 26,
        borderRadius: 1.5, bgcolor: SPLIT_YELLOW, color: SPLIT_INK }} />
    </Paper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UnifiedSearch — one field for handles AND emails. Invites are email-only;
// display name is auto-generated downstream (Jane4827-style).
// ═══════════════════════════════════════════════════════════════════════════
export function UnifiedSearch({ creatorList, rows, presets, onPickCreator, onAddInvite }) {
  const [q, setQ] = useState("");

  const trimmed = q.trim();
  const isEmail = isEmailish(trimmed);
  const looksLikeEmail = trimmed.includes("@") && !trimmed.startsWith("@");

  const results = useMemo(() => {
    if (trimmed.length < 2) return [];
    const lower = trimmed.toLowerCase().replace(/^@/, "");
    return creatorList
      .filter((c) => !rows.some((r) => r.creator?.creator === c.creator))
      .filter((c) =>
        (c.creator || "").toLowerCase().includes(lower) ||
        (c.display_name || "").toLowerCase().includes(lower) ||
        (c.email || "").toLowerCase().includes(lower))
      .slice(0, 6);
  }, [trimmed, creatorList, rows]);

  const lowerQ = trimmed.toLowerCase().replace(/^@/, "");
  const hitByEmail = (c) => looksLikeEmail && (c.email || "").toLowerCase().includes(lowerQ);

  const exactEmailOwner = isEmail
    ? creatorList.find((c) => c.email && c.email.toLowerCase() === trimmed.toLowerCase())
    : null;

  const emailTaken = isEmail &&
    rows.some((r) => r.email && r.email.toLowerCase() === trimmed.toLowerCase());
  const showInvite = isEmail && !exactEmailOwner && !emailTaken;
  const noMatch = trimmed.length >= 2 && results.length === 0 && !showInvite && !emailTaken;

  const pick = (c) => { onPickCreator(c); setQ(""); };
  const invite = () => {
    if (!isEmail || emailTaken) return;
    onAddInvite({ email: trimmed });
    setQ("");
  };

  return (
    <Box sx={{ animation: `${fadeUp} 0.22s ease` }}>
      <TextField fullWidth autoFocus size="small" value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name, @handle, or email"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {looksLikeEmail
                ? <EmailIcon sx={{ fontSize: 18, color: SPLIT_YELLOW_DARK }} />
                : <SearchIcon sx={{ fontSize: 18, color: trimmed ? SPLIT_YELLOW_DARK : "text.disabled" }} />}
            </InputAdornment>
          ),
          sx: { borderRadius: 2.5 },
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          if (results.length === 1) pick(results[0]);
          else if (showInvite) invite();
        }}
      />

      {results.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {results.map((c, i) => (
            <CreatorResultRow key={c.creator} creator={c} onPick={pick}
              delay={i * 0.04} matchedEmail={hitByEmail(c)}
              preset={presets[presetKeyForCreator(c)] || null} />
          ))}
        </Stack>
      )}

      {showInvite && (
        <Paper variant="outlined" sx={{ mt: 1.25, p: 2, borderRadius: 2.5,
          borderColor: SPLIT_BORDER, bgcolor: alpha(SPLIT_YELLOW, 0.025),
          animation: `${fadeUp} 0.2s ease` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <EmailIcon sx={{ fontSize: 14, color: SPLIT_YELLOW_DEEP, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: SPLIT_YELLOW_DEEP,
                display: "block" }}>
                {results.length > 0 ? "Not one of these? Invite" : "Invite to the split"} — {trimmed}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.64rem" }}>
                Display name is generated automatically from the email
              </Typography>
            </Box>
            {presets[presetKeyForEmail(trimmed)] && (
              <Chip icon={<PresetOnIcon sx={{ fontSize: "10px !important", color: `${SPLIT_INK} !important` }} />}
                label="Has a saved default" size="small"
                sx={{ height: 16, fontSize: "0.56rem", fontWeight: 700, bgcolor: SPLIT_TINT,
                  color: SPLIT_INK, "& .MuiChip-label": { px: 0.6 } }} />
            )}
            <Button variant="contained" size="small" onClick={invite}
              sx={{ borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
                bgcolor: SPLIT_YELLOW, color: SPLIT_INK,
                "&:hover": { bgcolor: SPLIT_YELLOW_DARK } }}>
              Add
            </Button>
          </Box>
        </Paper>
      )}

      {emailTaken && (
        <Alert severity="info" sx={{ mt: 1.25, borderRadius: 2 }}>
          <Typography variant="caption">{trimmed} is already on this split.</Typography>
        </Alert>
      )}

      {noMatch && (
        <Paper variant="outlined" sx={{ mt: 1.25, p: 1.75, borderRadius: 2.5,
          animation: `${fadeUp} 0.2s ease` }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {looksLikeEmail
              ? <>No creator with an email matching <strong>"{trimmed}"</strong> — finish typing the full address to invite them.</>
              : <>No creator named <strong>"{trimmed}"</strong> — type their <strong>email</strong> to invite them to the split.</>}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AmountCard — one per member: share, %, ⚡ pre-accept, preset bookmark
// ═══════════════════════════════════════════════════════════════════════════
export function AmountCard({ row, total, onAmount, onTogglePre, onSavePreset, hasPreset, deferred }) {
  const amt = parseLocalFloat(row.amount) || 0;
  const pct = !deferred && total > 0 && amt > 0 ? (amt / total) * 100 : null;
  const isMe = row.kind === "me";
  const isInvite = row.kind === "invite";
  const photo = isMe ? null : (row.creator?.pfphref || null);
  const accent = isMe ? SPLIT_INK : SPLIT_YELLOW_DARK;
  const canPre = !isMe && !deferred;
  const canPreset = !isMe && !!presetKeyFor(row);

  return (
    <Paper variant="outlined" sx={{
      p: 1.5, borderRadius: 2.5, display: "flex", alignItems: "center", gap: 1.25,
      borderColor: row.blitzpayPre ? SPLIT_BORDER : alpha(accent, 0.25),
      bgcolor: row.blitzpayPre ? alpha(SPLIT_YELLOW, 0.04) : alpha(accent, 0.015),
      transition: "all 0.15s ease", animation: `${fadeUp} 0.2s ease`,
      "&:hover": { borderColor: alpha(accent, 0.5) },
    }}>
      <Avatar src={photo || undefined} sx={{
        width: 34, height: 34, fontSize: "0.85rem", fontWeight: 800, flexShrink: 0,
        bgcolor: isMe ? "grey.800" : alpha(SPLIT_YELLOW, 0.15),
        color: isMe ? "common.white" : SPLIT_YELLOW_DEEP,
      }}>
        {!photo && (isMe ? "Y" : (row.name?.[0] || "?").toUpperCase())}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }} noWrap>
          {isMe ? "You" : row.name}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.64rem",
          color: row.blitzpayPre || row.presetApplied ? SPLIT_YELLOW_DEEP : "text.disabled",
          fontWeight: row.blitzpayPre || row.presetApplied ? 700 : 400 }} noWrap>
          {isMe ? "Your share"
            : row.blitzpayPre && row.presetApplied ? "⚡ Default applied · BlitzPay pre-accepted"
            : row.blitzpayPre ? "⚡ BlitzPay pre-accepted · fee off their slice"
            : row.presetApplied ? "Default applied"
            : isInvite ? row.email : "Paid at settlement"}
        </Typography>
      </Box>

      {canPreset && (
        <Tooltip arrow title={hasPreset
          ? "Saved default for this person — tap to clear it"
          : deferred
            ? `Save their default${row.blitzpayPre ? " — ⚡ BlitzPay" : ""}${row.amount && parseLocalFloat(row.amount) > 0 ? ` · ${fmt(parseLocalFloat(row.amount))}` : ""} for every future Split (works without a price)`
            : `Save ${row.amount && parseLocalFloat(row.amount) > 0 ? fmt(parseLocalFloat(row.amount)) : "their default"}${row.blitzpayPre ? " + ⚡ BlitzPay" : ""} for every future Split${row.amount && parseLocalFloat(row.amount) > 0 ? "" : " (no price needed)"}`}>
          <IconButton size="small" onClick={() => onSavePreset(row)}
            sx={{ p: 0.5, color: hasPreset ? SPLIT_YELLOW_DARK : "text.disabled", flexShrink: 0,
              "&:hover": { color: SPLIT_YELLOW_DARK, bgcolor: SPLIT_TINT } }}>
            {hasPreset ? <PresetOnIcon sx={{ fontSize: 17 }} /> : <PresetOffIcon sx={{ fontSize: 17 }} />}
          </IconButton>
        </Tooltip>
      )}

      {canPre && (
        <Tooltip arrow title={row.blitzpayPre
          ? "BlitzPay pre-accepted — their slice pays out early, conversion fee comes off their share. Tap to undo."
          : "Pre-accept BlitzPay for them — elects the early payout on their slice so they don't have to. Fee still comes off their share."}>
          <Chip
            icon={<Bolt sx={{ fontSize: "13px !important",
              color: row.blitzpayPre ? `${SPLIT_INK} !important` : `${SPLIT_YELLOW_DARK} !important` }} />}
            label="BlitzPay"
            size="small"
            onClick={() => onTogglePre(row.rowId)}
            sx={{
              height: 24, fontSize: "0.64rem", fontWeight: 800, cursor: "pointer", flexShrink: 0,
              bgcolor: row.blitzpayPre ? SPLIT_YELLOW : "transparent",
              color: row.blitzpayPre ? SPLIT_INK : SPLIT_YELLOW_DEEP,
              border: "1.5px solid",
              borderColor: row.blitzpayPre ? SPLIT_YELLOW : alpha(SPLIT_YELLOW_DARK, 0.4),
              transition: "all 0.18s ease",
              "&:hover": { bgcolor: row.blitzpayPre ? SPLIT_YELLOW_DARK : SPLIT_TINT },
            }} />
        </Tooltip>
      )}

      {deferred ? (
        <Chip icon={<LaterIcon sx={{ fontSize: "12px !important" }} />} label="Share TBD" size="small"
          sx={{ height: 24, fontSize: "0.66rem", fontWeight: 700, flexShrink: 0,
            bgcolor: alpha(SPLIT_YELLOW, 0.12), color: SPLIT_YELLOW_DEEP,
            border: "1px dashed", borderColor: alpha(SPLIT_YELLOW_DARK, 0.4),
            "& .MuiChip-icon": { color: SPLIT_YELLOW_DEEP } }} />
      ) : (
        <>
          <Chip
            label={pct ? `${pct % 1 ? pct.toFixed(1) : pct.toFixed(0)}%` : "—"}
            size="small"
            sx={{ height: 20, fontSize: "0.62rem", fontWeight: 800, minWidth: 44,
              bgcolor: pct ? SPLIT_TINT : "grey.100", color: pct ? SPLIT_INK : "text.disabled" }} />
          <TextField size="small" type="number" placeholder="0.00" value={row.amount}
            onChange={(e) => onAmount(row.rowId, e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { borderRadius: 2, fontWeight: 700, "& input": { textAlign: "right" } },
            }}
            sx={{ width: 120, flexShrink: 0 }} />
        </>
      )}
    </Paper>
  );
}